import Image from 'next/image';
import {useRouter} from 'next/router';
import {useIntl} from 'react-intl';
import {isValidMotionProp, motion} from 'framer-motion';
import {Center, CenterProps, chakra, Divider, Flex, FlexProps, Grid, Icon, Text} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {useRegionSettings} from '@eksab/hooks';
import {CoinsIcon} from '@eksab/assets/icons';

import {PrizeIcon} from './PrizeIcon';
import {StopwatchIcon, TargetIcon, gamePadIcon} from '../icons';
import {useSetChallengeQueryData} from '../hooks';
import type {Challenge} from '../types';
import {TriviaGame} from '@eksab/features/play/types';

interface ChallengeCardProps extends FlexProps {
  challenge: Challenge;
  previewOnly?: boolean;
}

export function ChallengeCard({challenge, previewOnly = false, ...props}: ChallengeCardProps) {
  const router = useRouter();
  const format = useIntl().formatNumber;

  const t = useTranslate();
  const {data: region} = useRegionSettings();
  const setChallengeQueryData = useSetChallengeQueryData();

  const {
    title,
    prize,
    no_of_questions: questionsCount,
    duration_per_question: questionDuration,
    entry_fee: fee,
    sponsors: [sponsor],
  } = challenge;

  const isWe = challenge.game == 'trivia-misc-we';

  const onChallengeClick = () => {
    if (previewOnly) return;

    mixpanel?.track('Trivia - Challenge Card Clicked', {
      id: challenge.id,
      challenge: title,
      fee,
      prize_type: prize?.type,
      prize_amount: prize?.amount,
    });

    setChallengeQueryData(challenge);
    router.push({query: {...router.query, 'challenge-id': challenge.id}}, undefined, {
      shallow: true,
      scroll: false,
    });
  };

  return (
    <>
      <Flex
        as="article"
        flexDir="column"
        gap={{base: 2, lg: 3}}
        align="center"
        px="6"
        bg="#1c1829"
        rounded="2rem"
        overflow="hidden"
        cursor={previewOnly ? 'default' : 'pointer'}
        {...props}
        onClick={onChallengeClick}
      >
        <Flex w="full" justify="center" align="flex-start" mb="2">
          <ChallengeName flex={sponsor ? undefined : 1} title={title} maxW="18rem">
            {title}
          </ChallengeName>

          {sponsor && <Sponsor sponsor={sponsor} />}
        </Flex>

        <Grid flex={1} alignItems="center" placeContent="center" columnGap="2.5" w="90%">
          <Center boxSize="3.75rem" gridRow="span 2">
            {isWe || !prize ? <Image src={gamePadIcon} alt="" height="52" /> : <PrizeIcon type={prize.type} h="12" />}
          </Center>

          {!!prize && !isWe && (
            <Text as="span" color="#8f8f8f" fontSize="xs" fontWeight="600" gridColumn="2/3">
              {t('app.actions.win')}
            </Text>
          )}

          {isWe ? (
            <Text
              color="#edb35c"
              fontSize="2xl"
              fontWeight="700"
              noOfLines={2}
              justifySelf="center"
              gridColumn="2/3"
              gridRow="span 2"
            >
              {t('trivia.challenge.play-and-enjoy')}
            </Text>
          ) : !!prize ? (
            <Text title={prize.name} color="#edb35c" fontSize="2xl" fontWeight="700" noOfLines={2}>
              {prize.type === 'physical' ? prize.name : format(prize.amount)}

              {prize.type !== 'physical' && (
                <Text as="span" color="white" fontSize="md" fontWeight="600">
                  {''} {prize.type === 'cash' ? region?.currency : t('app.coins')}
                </Text>
              )}
            </Text>
          ) : (
            <Text
              color="#edb35c"
              fontSize="2xl"
              fontWeight="700"
              noOfLines={2}
              justifySelf="center"
              gridColumn="2/3"
              gridRow="span 2"
            >
              {t('trivia.challenge.free-trial')}
            </Text>
          )}
        </Grid>

        <Center gap="6" bg="#2a2e3a" px="2" py="1.5" rounded="full" w="full" maxW="32rem" mb={previewOnly ? 4 : 1}>
          <Grid columnGap="3" alignItems="center">
            <TargetIcon width="auto" height="5" gridRow="span 2" />

            <Text gridColumn="2/3" color="white" fontSize="lg" fontWeight="700">
              {format(questionsCount)}{' '}
              <Text as="span" color="#bebebe" fontSize="x-small" fontWeight="600">
                {`/ ${format(questionsCount)}`}
              </Text>
            </Text>

            <Text as="span" color="#8f8f8f" fontSize="x-small" fontWeight="600">
              {t('trivia.challenge.target')}
            </Text>
          </Grid>

          <Divider orientation="vertical" h="30%" opacity="0.3" />

          <Grid columnGap="3" alignItems="center">
            <StopwatchIcon width="auto" height="5" gridRow="span 2" />

            <Text gridColumn="2/3" color="white" fontSize="md" fontWeight="700">
              {format(questionDuration)}{' '}
              <Text as="span" color="#bebebe" fontSize="xs" fontWeight="600">
                {t('trivia.time.seconds')}
              </Text>
            </Text>

            <Text as="span" color="#8f8f8f" fontSize="x-small" fontWeight="600">
              {t('trivia.challenge.per-question')}
            </Text>
          </Grid>
        </Center>

        {!previewOnly && <PlayNowButton game={challenge.game} fee={fee} w={['90%', '80%']} maxW="28rem" />}
      </Flex>
    </>
  );
}

const ChallengeName = ({children, ...props}: CenterProps) => (
  <Center color="#e1ecdd" pos="relative" isolation="isolate" minHeight="2.125rem" {...props}>
    <Icon
      pos="absolute"
      inset="0"
      zIndex="-1"
      boxSize="full"
      viewBox="0 0 248 33"
      preserveAspectRatio="none"
      px="inherit"
      color="#2a2e3a"
    >
      <path
        d="M0 0H248L239.913 25.9729C238.611 30.1527 234.743 33 230.365 33H17.4042C12.9965 33 9.10861 30.1141 7.83251 25.8952L0 0Z"
        fill="currentColor"
      />
    </Icon>

    <Text as="h2" color="inherit" fontSize="sm" fontWeight="600" px="4" noOfLines={1}>
      {children}
    </Text>
  </Center>
);

interface SponsorProps extends CenterProps {
  sponsor: Challenge['sponsors'][number];
}

const Sponsor = ({sponsor, ...props}: SponsorProps) => {
  const t = useTranslate();

  return (
    <Center pos="relative" isolation="isolate" py="1" {...props}>
      <Icon
        pos="absolute"
        inset="0"
        zIndex="-1"
        boxSize="full"
        preserveAspectRatio="none"
        px="inherit"
        viewBox="0 0 114 30"
        fill="none"
        color="white"
      >
        <path
          d="M0 0H114L110.606 21.5553C109.841 26.4178 105.65 30 100.728 30H13.1751C8.23607 30 4.03678 26.3941 3.29008 21.5118L0 0Z"
          fill="currentColor"
        />
      </Icon>

      <Flex align="center" gap="1" px="3">
        <Text as="span" color="inherit" fontSize="sm" fontWeight="600">
          {t('trivia.sponsor.by')}
        </Text>

        {sponsor.logo && (
          <Center boxSize="6" p="0.5" rounded="full" bg="#d6d6d6" overflow="hidden">
            <Image src={sponsor.logo.thumb ?? sponsor.logo.original} alt="" width="18" height="18" />
          </Center>
        )}

        <Text as="span" color="inherit" fontSize="xs" fontWeight="700">
          {sponsor.name}
        </Text>
      </Flex>
    </Center>
  );
};

const PlayNowButton = ({fee, game, ...props}: CenterProps & {fee: number; game: TriviaGame}) => {
  const format = useIntl().formatNumber;

  const t = useTranslate();

  const isWe = game == 'trivia-misc-we';

  return (
    <Center as="button" pos="relative" isolation="isolate" minWidth="14rem" minH="2.7rem" {...props}>
      <Icon
        pos="absolute"
        inset="0"
        zIndex="-1"
        boxSize="full"
        viewBox="0 0 260 43"
        preserveAspectRatio="none"
        px="inherit"
      >
        <path
          d="M0 43H260L252.326 14.7559C249.959 6.04567 242.052 0 233.026 0H28.0084C19.2297 0 11.4772 5.72462 8.89394 14.1146L0 43Z"
          fill="#5ac57b"
        />
      </Icon>

      <Flex align="center" gap="4" px="5" color="#2a2e3a">
        <Text color="inherit" fontSize="sm" fontWeight="800">
          {t('app.actions.play.now')}
        </Text>

        {!isWe && (
          <Flex
            align="center"
            gap="0.5"
            ps="2"
            pe="6"
            py="0.5"
            bg="white"
            rounded="full"
            fontSize="0.5rem"
            fontWeight="600"
          >
            <CoinsIcon w="4" me="1" />

            <Text as="span" color="inherit" fontSize="sm" fontWeight="800">
              {!!fee && format(fee)}
            </Text>

            {!!fee ? t('competition.overview.fee') : t('trivia.challenge.no-fees')}
          </Flex>
        )}
      </Flex>
    </Center>
  );
};

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) => isValidMotionProp(prop) || prop === 'children',
});

const Skeleton = () => (
  <ChakraBox
    style={{backgroundColor: '#f3f3f3'}}
    w="full"
    h={{base: '14.2rem', lg: '14.95rem'}}
    rounded="2rem"
    transition={{repeat: 'Infinity', repeatType: 'reverse', duration: '1'}}
    animate={{backgroundColor: '#ecebeb'}}
  />
);

ChallengeCard.Skeleton = Skeleton;
